import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import { HourglassEmpty } from "@mui/icons-material"
import Button from "@mui/material/Button"
import React from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { PATHS } from "../../../constants/paths"
import { formatCountdown } from "../utils/formatCountdown"
import { useCountdown } from "../utils/useCountdown"
import { connectHandler } from "../../../utils/connectHandler"
import { useAppDispatch, useAppSelector } from "../../../store"
import { useTournamentDialogs } from "../hooks/useTournamentDialogs"
import { TournamentActionButton } from "./TournamentActionButton"
import { Grid } from "@mui/material"

export const TournamentCard = ({ tournament }) => {
  const remainingSeconds = useCountdown(tournament)

  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.user)

  const { openJoin, openGame } = useTournamentDialogs({
    tournament,
    user,
    successBackPath: PATHS.TOURNAMENTS.PATH,
  })

  const tournamentIsLive = tournament.starts_in === null

  return (
    <Box
      onClick={() =>
        navigate(`${PATHS.TOURNAMENT_DETAILS.PATH}${tournament.id}`)
      }
      sx={{
        p: 1,
        border: "1px solid rgba(198, 146, 76, 0.25)",
        borderRadius: 1,
        background: `url(${tournament.image}) top / contain no-repeat, #000`,
        minHeight: {
          xs: 450,
          sm: 550,
          md: 600,
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        cursor: "pointer",
        position: "relative",
      }}
    >
      {tournament?.is_banned && (
        <Box sx={{ position: "absolute", top: 0, left: 12 }}>
          <Typography
            sx={{
              color: "#E60F04",
              fontSize: 16,
              fontWeight: 900,
              textAlign: "center",
              mt: 1,
            }}
          >
            You are banned
          </Typography>
        </Box>
      )}

      {/* Статус-Бейдж (LIVE или STARTING SOON) */}
      <Box sx={{ position: "absolute", top: 12, right: 12 }}>
        {tournamentIsLive ? (
          <Box
            sx={{
              filter: "drop-shadow(0 0 3px #00ff00)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                clipPath:
                  "polygon(8px 0,calc(100% - 8px) 0,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px)",
                px: 1.5,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#00ff00",
                  boxShadow: "0 0 6px #00ff00",
                }}
              />
              <Typography
                sx={{
                  color: "#dedede",
                  fontSize: 14,
                  fontWeight: "bold",
                  letterSpacing: "1px",
                }}
              >
                LIVE
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              filter: "drop-shadow(0 0 3px #cca01d)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                clipPath:
                  "polygon(8px 0,calc(100% - 8px) 0,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px)",
                px: 1.5,
                py: 0.5,
              }}
            >
              <HourglassEmpty sx={{ color: "#cca01d", fontSize: 16 }} />
              <Typography
                sx={{
                  color: "#dedede",
                  fontSize: 14,
                  fontWeight: "bold",
                  letterSpacing: "1px",
                }}
              >
                STARTING SOON
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Card body */}
      <Box
        sx={{
          minHeight: {
            xs: 150,
            md: 200,
          },
          p: {
            xs: 1.5,
            md: 2,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Typography
          sx={{
            color: "#c7954f",
            fontSize: 14,
            fontWeight: 900,
            letterSpacing: "0.08em",
          }}
        >
          {tournament.type.toUpperCase()}
        </Typography>

        <Typography
          sx={{
            mt: 0.5,
          }}
          variant="h4"
        >
          {tournament.title}
        </Typography>

        <Typography
          sx={{
            mt: 0.7,
            color: "#dedede",
            fontSize: 16,
            lineHeight: 1.6,
          }}
        >
          {tournament.description}
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            py: 1,
            mt: 0.5,
            borderTop: "1px solid rgba(198,146,76,0.14)",
            borderBottom: "1px solid rgba(198,146,76,0.14)",
          }}
        >
          {[
            {
              label: "PRIZE POOL",
              value: tournament.prize_pool + " USDT",
              isFee: true,
            },
            {
              label: "ENTRY FEE",
              value:
                tournament.entry_fee > 0
                  ? tournament.entry_fee + " USDT"
                  : "Free",
              isFee: true,
            },
            { label: "PLAYERS", value: tournament.players, isFee: false },
          ].map(({ label, value, isFee }) => (
            <Box key={label}>
              <Typography
                sx={{
                  color: isFee ? "#c49654" : "#999",
                  fontSize: 14,
                  fontWeight: 900,
                  letterSpacing: "0.06em",
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  color: "#e4ded1",
                  fontSize: 18,
                  fontWeight: 800,
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "end",
            gap: 0.8,
            mt: 1.7,
            color: "#d79b4d",
          }}
        >
          <AccessTimeOutlinedIcon sx={{ fontSize: 17, color: "#ccc" }} />
          <Box>
            <Typography
              sx={{
                color: "#c49654",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                letterSpacing: "0.06em",
              }}
            >
              {tournamentIsLive ? "TIME LEFT" : "STARTS IN"}
            </Typography>
            <Typography
              sx={{
                mt: 0.3,
                color: "#eee",
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {formatCountdown(remainingSeconds)}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* end of Card body */}

      {/* Card footer */}
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ cursor: "default", mx: 1 }}
      >
        <Grid container spacing={1}>
          <Grid item xs={6} sm={12} md={12} lg={6}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ mt: 2 }}
              component={NavLink}
              to={`${PATHS.TOURNAMENT_DETAILS.PATH}${tournament.id}`}
            >
              View details
            </Button>
          </Grid>
          <Grid item xs={6} sm={12} md={12} lg={6}>
            <TournamentActionButton
              tournament={tournament}
              user={user}
              onConnect={() => connectHandler(dispatch)}
              onJoin={openJoin}
              onPlay={openGame}
              sx={{
                mt: {
                  xs: 2,
                  sm: 0,
                  lg: 2,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
      {/* end of Card Footer */}
    </Box>
  )
}
